<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email, helpers } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  phoneNumber: {
    type: String,
    default: '',
  },
  thumbnail: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentEmail = ref(props.email);
const agentPhone = ref(props.phoneNumber);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const avatarFile = ref(null);
const avatarUrl = ref(props.thumbnail || '');
const agentCredentials = computed(() => ({ email: agentEmail.value }));

const chinaMobile = helpers.withMessage(
  () => t('AGENT_MGMT.ADD.FORM.PHONE_NUMBER.ERROR'),
  value => {
    if (!value) return true;
    const digits = String(value)
      .replace(/\D/g, '')
      .replace(/^86/, '');
    return /^1[3-9]\d{9}$/.test(digits);
  }
);

const rules = {
  agentName: { required, minLength: minLength(1) },
  agentEmail: { required, email },
  agentPhone: { chinaMobile },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  agentPhone,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const appendFormData = (formData, key, value) => {
  if (value === undefined || value == null || value === '') return;
  formData.append(key, value);
};

const buildPayload = () => {
  const payload = {
    id: props.id,
    name: agentName.value,
    email: agentEmail.value,
    phone_number: agentPhone.value,
    availability: agentAvailability.value,
  };

  if (selectedRole.value.name.startsWith('custom_')) {
    payload.custom_role_id = selectedRole.value.id;
  } else {
    payload.role = selectedRole.value.name;
    payload.custom_role_id = null;
  }

  if (!avatarFile.value) return payload;

  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (key === 'custom_role_id') {
      appendFormData(formData, 'custom_role_id', value);
      return;
    }
    appendFormData(formData, `agent[${key}]`, value);
  });
  formData.append('agent[avatar]', avatarFile.value);
  return formData;
};

const handleAvatarUpload = ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;
};

const handleAvatarDelete = () => {
  avatarFile.value = null;
  avatarUrl.value = '';
};

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = buildPayload();
    if (payload instanceof FormData) {
      await store.dispatch('agents/update', { id: props.id, formData: payload });
    } else {
      await store.dispatch('agents/update', payload);
    }
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="flex flex-col items-start w-full" @submit.prevent="editAgent">
      <div class="w-full mb-4">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_MGMT.ADD.FORM.AVATAR.LABEL') }}
        </span>
        <div class="mt-2">
          <Avatar
            :src="avatarUrl || ''"
            :name="agentName || ''"
            :size="72"
            allow-upload
            @upload="handleAvatarUpload"
            @delete="handleAvatarDelete"
          />
        </div>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentPhone.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.PHONE_NUMBER.LABEL') }}
          <input
            v-model="agentPhone"
            type="tel"
            maxlength="13"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.PHONE_NUMBER.PLACEHOLDER')"
            @input="v$.agentPhone.$touch"
          />
          <span v-if="v$.agentPhone.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.PHONE_NUMBER.ERROR') }}
          </span>
          <span v-else class="text-xs text-n-slate-10">
            {{ $t('AGENT_MGMT.ADD.FORM.PHONE_NUMBER.HELP') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
